import { useMemo } from 'react';
import { unmaskMoney } from '../../shared/utils/masks';
import { createMoneySchema, createTestSchema } from '../../shared/utils/schemas';
import { validatePostalCode } from '../../shared/utils/validation';
import * as y from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SIMULATION_STEPS } from '../simulation-steps';
import { useSimuladorEnergiaSolarState } from '../state';

const ProjectSchema = {
  projectValue: createMoneySchema({ min: 500_00, max: 5_000_000_00, required: true }),
  entryValue: createMoneySchema({
    min: 200_00,
    max: 1_000_000_00,
    required: false,
    allowZeros: true,
  }).concat(
    y
      .string()
      .test(
        'entryValueRule',
        'Valor de entrada deve ser menor que o valor do projeto',
        (value, ctx) => {
          const projectValue = ctx.parent.projectValue;
          const unmaskedEntryValue = unmaskMoney(value || '0');
          const unmaskedProjectValue = unmaskMoney(projectValue || '0');

          if (unmaskedEntryValue === 0) {
            return true;
          }

          return unmaskedEntryValue < unmaskedProjectValue;
        },
      ),
  ),
  store: y.string().required('Campo obrigatório'),
};

const WithoutProjectSchema = {
  roofType: y.string().required('Campo obrigatório'),
  postalCode: createTestSchema({
    validate: validatePostalCode,
    required: true,
    messages: {
      invalid: 'CEP inválido',
      required: 'CEP é obrigatório',
    },
  }),
};

const AddressSchema = {
  address: createTestSchema({ required: true }),
  addressNumber: createTestSchema({
    validate: (v, ctx) => {
      if (ctx.parent.noAddressNumber) {
        return true;
      }

      return !!v && v.trim() !== '';
    },
  }),
  neighborhood: createTestSchema({ required: true }),
  city: createTestSchema({ required: true }),
  state: createTestSchema({ required: true }),
};

export function useInitialDataSchema({ showAddressFields, addressCompleted }) {
  const { hasSelectedType, simulationType, simulationStep } = useSimuladorEnergiaSolarState();

  const validation = useMemo(() => {
    let schemaShape = {};

    if (!hasSelectedType) {
      return yupResolver(y.object().shape({}));
    }

    if (simulationStep?.id === SIMULATION_STEPS.FORM_USAGE.id) {
      return yupResolver(
        y.object().shape({
          averageUsage: createMoneySchema({ min: 200_00, max: 20000_00, required: true }),
        }),
      );
    }

    if (simulationType === 'existing') {
      schemaShape = {
        ...schemaShape,
        ...ProjectSchema,
      };
    }

    if (simulationType === 'new') {
      schemaShape = {
        ...schemaShape,
        ...WithoutProjectSchema,
      };

      if (showAddressFields) {
        schemaShape = {
          ...schemaShape,
          ...AddressSchema,
        };
      }

      if (addressCompleted) {
        schemaShape = {
          ...schemaShape,
          searchStore: y
            .string()
            .test('test-store-search', 'Por favor, digite um CEP válido', (value) => {
              return validatePostalCode(value);
            })
            .test('test-store', (value, ctx) => {
              if (!ctx.parent.store || ctx.parent.store === '' || ctx.parent.store === '0') {
                if (ctx.parent.storeCount === 0) {
                  return ctx.createError({
                    message: 'Nenhuma loja encontrada para o CEP informado',
                  });
                }

                return ctx.createError({ message: 'Por favor, selecione uma loja' });
              }

              return true;
            }),
        };
      }
    }

    return yupResolver(y.object().shape(schemaShape));
  }, [addressCompleted, hasSelectedType, showAddressFields, simulationStep?.id, simulationType]);

  return validation;
}
